import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoveRight } from 'lucide-react';
import { LOT_MAPPING_RULES } from './lotMappingRulesRegistry';

interface Props {
  open: boolean;
  /** Code du lot (`MigrationStepDefinition.code`) dont afficher les règles. */
  lotCode: string | null;
  /** Nom lisible du lot, pour le titre. */
  lotName?: string;
  onClose: () => void;
}

/**
 * Dialog informatif listant les règles de mapping V1 → V2 d'un lot (lecture
 * seule). Alimenté par `LOT_MAPPING_RULES` ; déclenché par le bouton « Règles
 * de mapping » d'une ligne de `LotsTable`.
 */
export function MappingRulesDialog({ open, lotCode, lotName, onClose }: Props) {
  const entry = lotCode ? LOT_MAPPING_RULES[lotCode] : undefined;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            Règles de mapping{lotName ? ` — ${lotName}` : ''}
          </DialogTitle>
          {entry?.intro && <DialogDescription>{entry.intro}</DialogDescription>}
        </DialogHeader>

        {entry ? (
          <ul className="mt-1 max-h-[60vh] divide-y divide-gray-100 overflow-y-auto pr-1 dark:divide-slate-700">
            {entry.rules.map((rule) => (
              <li key={rule.field} className="py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {rule.field}
                  </span>
                  <MoveRight className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                  <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-violet-700 dark:bg-slate-800 dark:text-violet-300">
                    {rule.source}
                  </code>
                </div>
                {rule.note && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-slate-400">
                    {rule.note}
                  </p>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Aucune règle documentée pour ce lot.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
